import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Environment } from '@/hooks/useProjects';

interface EnvironmentSelectProps {
  environments: Environment[];
  value: string | null;
  onChange: (environmentId: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function EnvironmentSelect({
  environments,
  value,
  onChange,
  placeholder = 'Select environment',
  className,
  disabled = false,
}: EnvironmentSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedEnvironment = environments.find((e) => e.id === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 w-full text-left bg-muted/30 border border-border rounded-lg transition-colors',
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-muted/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-ring/20'
        )}
      >
        <Layers className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className={cn('flex-1 truncate text-sm', !selectedEnvironment && 'text-muted-foreground')}>
          {selectedEnvironment ? selectedEnvironment.name : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="max-h-48 overflow-y-auto py-1">
            {environments.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                No environments available
              </div>
            ) : (
              environments.map((environment) => (
                <button
                  key={environment.id}
                  onClick={() => {
                    onChange(environment.id);
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                >
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{environment.name}</span>
                  {value === environment.id && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
